import React from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Input,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  TextField,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Avatar,
  Select,
  MenuItem,
  InputLabel,
  Typography,
  Chip,
  Checkbox,
  OutlinedInput,
  Tooltip,
  IconButton,
  Snackbar,
  Alert,
  Autocomplete,
} from "@mui/material";
import { tokens } from "../theme";
import { useState, useEffect } from "react";
import Header from "../components/Header";
import { toast } from "react-toastify";
import {
  Add,
  Delete,
  MailOutline,
  MarkEmailRead,
  Visibility,
} from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import debounce from "lodash.debounce";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { TrendingUp, Schedule, Cancel } from "@mui/icons-material";
import { useUser } from "../provider/AuthProvider";
import axios from "axios";
import BreadcrumbsBar from "../components/BreadcrumbsBar";
import ConfirmDialog from "../components/common/ComfirmationDialog";
import stringToColor from "../services/stringToColor";
import { useTranslation } from "react-i18next";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

const NotificationPage = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [title, setTitle] = useState("");
  const [isInbox, setIsInbox] = useState("inbox");
  const [message, setMessage] = useState("");
  const [toUserIds, setToUserIds] = useState("");
  const [sendNow, setSendNow] = useState(true);
  const [scheduleAt, setScheduleAt] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [inbox, setInbox] = useState([]);
  const [outbox, setOutbox] = useState([]);
  const [pageSize, setPageSize] = useState(10);
  const [filterType, setFilterType] = useState("");
  const [filterDate, setFilterDate] = useState("1y");
  const [userList, setUserList] = useState([]);
  const [userFilter, setUserFilter] = useState({ search: "" });
  const [recipientScope, setRecipientScope] = useState("everyone");
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const [customDateValid, setCustomDateValid] = useState(true);
  const [stats, setStats] = useState({
    scheduled: 0,
    delivered: 0,
    undelivered: 0,
  });
  const [todayScheduled, setTodayScheduled] = useState([]);
  const { user, loading } = useUser();
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmit, setSubmit] = useState(false);

  const { i18n } = useTranslation();
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  useEffect(() => {
    if (!loading && user?.id) {
      axios
        .get(`${process.env.REACT_APP_BACKEND_URL}/api/notify/stats/${user.id}`)
        .then((res) => setStats(res.data))
        .catch(console.error);

      axios
        .get(
          `${process.env.REACT_APP_BACKEND_URL}/api/notify/today-scheduled/${user.id}`
        )
        .then((res) => setTodayScheduled(res.data))
        .catch(console.error);
    }
  }, [loading, user]);

  const handleSubmit = async () => {
    if (!title || !message || !toUserIds.length)
      return toast.error("All fields required");
    setSubmit(true);
    const payload = {
      title,
      message,
      type: "manual",
      send_type: sendNow ? "immediate" : "scheduled",
      schedule_at: sendNow ? null : scheduleAt,
      to_user_ids: toUserIds,
      from_user_id: user.id,
      type: "MAINTENANCE",
    };

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/notify/create`,
        payload,
        { headers: { "Content-Type": "application/json" } }
      );

      setSnack({
        open: true,
        message: "Notification is created.",
        severity: "success",
      });

      setTitle("");
      setMessage("");
      setToUserIds([]);
      fetchNotification();
      setOpenDialog(false);
    } catch (err) {
      setSnack({
        open: true,
        message: "Failed to send notification",
        severity: "error",
      });
    } finally {
      setSubmit(false);
    }
  };

  const fetchUserList = async (search = "") => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/users?search=${search}`
      );
      setUserList(res.data);
      setToUserIds([]);
    } catch (err) {
      console.error("Error fetching user list", err);
    }
  };

  const handleSearchChange = debounce((e) => {
    setUserFilter({ ...userFilter, search: e.target.value });
    fetchUserList(e.target.value);
  }, 500);

  useEffect(() => {
    fetchNotification();
    fetchUserList();
  }, [isInbox]);

  const fetchNotification = async () => {
    if (!user?.id) return;
    try {
      setIsLoading(true);
      const inboxRes = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/notify/inbox/${user.id}`
      );
      const outboxRes = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/notify/outbox/${user.id}`
      );
      setInbox(inboxRes.data);
      setOutbox(outboxRes.data);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const filterByDate = (dateString, filterKey) => {
    const date = new Date(dateString);
    const now = new Date();

    switch (filterKey) {
      case "today":
        return date.toDateString() === now.toDateString();
      case "1m":
        return date > new Date(now.setMonth(now.getMonth() - 1));
      case "3m":
        return date > new Date(now.setMonth(now.getMonth() - 3));
      case "1y":
        return date > new Date(now.setFullYear(now.getFullYear() - 1));
      case "custom":
        if (!customStartDate || !customEndDate) return true;
        const start = new Date(customStartDate);
        const end = new Date(customEndDate);
        return date >= start && date <= end;
      default:
        return true;
    }
  };

  useEffect(() => {
    filterByDate();
    const isDateRangeValid = () =>
      !customStartDate || !customEndDate || customStartDate <= customEndDate;
    setCustomDateValid(
      !customStartDate || !customEndDate || customStartDate <= customEndDate
    );
  }, [filterDate, customStartDate, customEndDate]);

  const handleView = (row) => {
    setSelectedNotification(row);
    setOpenViewDialog(true);
    if (!row.read) {
      axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/notify/mark-read/${row.id}`
      ).catch(console.error);
      fetchNotification();
    }
  };

  const handleDelete = async (id) => {
    setSelectedNotification(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/notify/delete/${selectedNotification}`
      );

      setInbox(inbox.filter((n) => n.id !== selectedNotification));
      setOutbox(outbox.filter((n) => n.id !== selectedNotification));
      setSnack({
        open: true,
        message: "Notification deleted",
        severity: "success",
      });
    } catch {
      setSnack({
        open: true,
        message: "Notification deleting failed",
        severity: "error",
      });
    } finally {
      setDeleteDialogOpen(false);
    }
  };

  const columns = [
    {
      field: "read",
      headerName: "",
      flex: 0.2,
      renderCell: (params) => (
        <Box>
          {params.row.read == true ? (
            <MailOutline fontSize="small" sx={{ mr: 0.5 }} />
          ) : (
            <MarkEmailRead fontSize="small" color="primary" sx={{ mr: 0.5 }} />
          )}
        </Box>
      ),
    },
    {
      field: "body",
      headerName: "Body",
      flex: 1.5,
      renderCell: (params) => (
        <Box>
          <Typography
            variant="h6"
            fontWeight={params.row.read ? "normal" : "bold"}
          >
            {params.row.title}
          </Typography>

          {/* <Tooltip title={params.row.message} placement="bottom-start">
            <Typography variant="body2" noWrap sx={{ maxWidth: 250 }}>
              {params.row.message}
            </Typography>
          </Tooltip> */}
        </Box>
      ),
    },
    {
      field: "send_type",
      headerName: "Type",
      flex: 0.7,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={params.value === "immediate" ? "success" : "warning"}
          size="small"
        />
      ),
    },
    {
      field: "createdAt",
      headerName: "Sent At",
      flex: 1,
      renderCell: (params) => (
        <Typography variant="h6">
          {new Date(params.row.createdAt).toLocaleString()}
        </Typography>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.5,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton onClick={() => handleView(params.row)} size="small">
            <Visibility fontSize="small" />
          </IconButton>
          <IconButton
            onClick={() => handleDelete(params.row.id)}
            size="small"
            color="error"
          >
            <Delete fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box className="main-container mt-4">
      <BreadcrumbsBar />
      <Box display="flex" justifyContent="space-around">
        <Grid
          container
          spacing={2}
          columns={{ xs: 2, sm: 8, md: 10 }}
          rows={{ xs: 3, sm: 3, md: 2 }}
          sx={{
            width: "100%",
          }}
        >
          <Grid item size={{ xs: 2, sm: 2, md: 2 }}>
            <Paper
              sx={{
                padding: 2,
                height: "80vh",
              }}
              elevation={1}
            >
              <Box display="flex" justifyContent="center" m={2}>
                <Button onClick={() => setOpenDialog(true)} variant="contained">
                  <Add /> Add Notification
                </Button>
              </Box>
              <FormControl
                component="fieldset"
                sx={{
                  border: "1px solid #ccc",
                  borderRadius: 1,
                  display: "flex",
                  p: 1, // padding inside border
                }}
              >
                <FormLabel component="legend">User Type</FormLabel>
                <RadioGroup
                  value={isInbox}
                  onChange={(e) => setIsInbox(e.target.value)}
                  row={true}
                >
                  <FormControlLabel
                    value="inbox"
                    control={<Radio />}
                    label="Inbox"
                  />
                  <FormControlLabel
                    value="outbox"
                    control={<Radio />}
                    label="Outbox"
                  />
                </RadioGroup>
              </FormControl>
              <Box mt={3}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                    label="Type"
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="immediate">Immediate</MenuItem>
                    <MenuItem value="scheduled">Scheduled</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Box mt={3}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Date</InputLabel>
                  <Select
                    value={filterDate}
                    onChange={(e) => setFilterDate(e.target.value)}
                    label="Date"
                  >
                    <MenuItem value="today">Today</MenuItem>
                    <MenuItem value="1m">1 Month</MenuItem>
                    <MenuItem value="3m">3 Months</MenuItem>
                    <MenuItem value="1y">1 Year</MenuItem>
                    <MenuItem value="custom">Custom</MenuItem>
                  </Select>
                  {filterDate === "custom" && (
                    <Box mt={2}>
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                          label="Start Date"
                          value={customStartDate}
                          onChange={(newValue) => setCustomStartDate(newValue)}
                          slotProps={{
                            textField: { fullWidth: true, margin: "dense" },
                          }}
                        />
                        <DatePicker
                          label="End Date"
                          value={customEndDate}
                          onChange={(newValue) => setCustomEndDate(newValue)}
                          slotProps={{
                            textField: { fullWidth: true, margin: "dense" },
                          }}
                        />
                      </LocalizationProvider>
                      {!customDateValid && (
                        <Typography variant="h6" color="error">
                          Invalid date range!
                        </Typography>
                      )}
                    </Box>
                  )}
                </FormControl>
              </Box>
            </Paper>
          </Grid>
          <Grid item size={{ xs: 2, sm: 2, md: 6 }}>
            <Paper
              sx={{
                padding: 2,
                height: "80vh",
              }}
              elevation={1}
            >
              <div style={{ height: "75vh" }}>
                <DataGrid
                  rows={(isInbox !== "inbox" ? inbox : outbox)
                    .filter((n) => !filterType || n.send_type === filterType)
                    .filter((n) => filterByDate(n.createdAt, filterDate))}
                  columns={columns}
                  getRowId={(row) => row.id}
                  pageSize={pageSize}
                  onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                  loading={isLoading}
                  pagination
                  // sx={{ overflowY: "auto" }}
                  initialState={{
                    pagination: {
                      paginationModel: {
                        pageSize: 10,
                      },
                    },
                  }}
                  pageSizeOptions={[5, 10, 15, 20, 25]}
                />
              </div>
            </Paper>
          </Grid>
          <Grid item size={{ xs: 2, sm: 2, md: 2 }}>
            {/* <Box display="flex" justifyContent="center"> */}
            <Stack
              direction="column"
              display="flex"
              justifyContent="space-evenly"
              spacing={5}
            >
              <Paper
                elevation={1}
                sx={{ p: 2, display: "flex", alignItems: "center", gap: 2 }}
              >
                <Schedule color="warning" />
                <Box>
                  <Typography variant="h6">Scheduled</Typography>
                  <Typography variant="h5" color="text.secondary">
                    {stats.scheduled}
                  </Typography>
                </Box>
              </Paper>
              <Paper
                elevation={1}
                sx={{ p: 2, display: "flex", alignItems: "center", gap: 2 }}
              >
                <TrendingUp color="success" />
                <Box>
                  <Typography variant="h6">Delivered</Typography>
                  <Typography variant="h5" color="text.secondary">
                    {stats.delivered}
                  </Typography>
                </Box>
              </Paper>
              <Paper
                elevation={1}
                sx={{ p: 2, display: "flex", alignItems: "center", gap: 2 }}
              >
                <Cancel color="error" />
                <Box>
                  <Typography variant="h6">Undelivered</Typography>
                  <Typography variant="h5" color="text.secondary">
                    {stats.undelivered}
                  </Typography>
                </Box>
              </Paper>
              <Paper
                sx={{
                  padding: 2,
                  height: "40vh",
                }}
                elevation={1}
              >
                <Typography variant="h6" mb={1}>
                  Today's Scheduled Notifications
                </Typography>
                {todayScheduled.length === 0 ? (
                  <Typography color="text.secondary">
                    No scheduled notifications for today.
                  </Typography>
                ) : (
                  <Paper
                    sx={{
                      padding: 2,
                      height: "40vh",
                    }}
                    elevation={1}
                  >
                    <Typography variant="h6" mb={1}>
                      Today's Scheduled Notifications
                    </Typography>
                    {todayScheduled.length === 0 ? (
                      <Typography color="text.secondary">
                        No scheduled notifications for today.
                      </Typography>
                    ) : (
                      <Box
                        sx={{
                          height: "30vh",
                          overflowY: "auto",
                          pr: 1,
                          scrollbarWidth: "thin",
                          "&::-webkit-scrollbar": {
                            width: "8px",
                          },
                          "&::-webkit-scrollbar-track": {
                            backgroundColor: "#f0f0f0",
                            borderRadius: "8px",
                          },
                          "&::-webkit-scrollbar-thumb": {
                            backgroundColor: "#c1c1c1",
                            borderRadius: "8px",
                            border: "2px solid #f0f0f0",
                          },
                          "&::-webkit-scrollbar-thumb:hover": {
                            backgroundColor: "#a0a0a0",
                          },
                        }}
                      >
                        {todayScheduled?.map((n) => (
                          <Card
                            key={n.id}
                            variant="outlined"
                            sx={{
                              mb: 2,
                              p: 2,
                              borderRadius: 2,
                              boxShadow: 1,
                              transition: "transform 0.2s ease",
                              "&:hover": {
                                transform: "scale(1.02)",
                                boxShadow: 4,
                              },
                            }}
                          >
                            <Stack
                              direction="row"
                              justifyContent="space-between"
                              alignItems="center"
                            >
                              <Box>
                                <Typography
                                  variant="subtitle1"
                                  fontWeight="bold"
                                >
                                  {n.title}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  To:{" "}
                                  {n.recipient_scope.charAt(0).toUpperCase() +
                                    n.recipient_scope.slice(1)}
                                </Typography>
                              </Box>
                              <Chip
                                label={new Date(n.schedule_at).toLocaleString()}
                                color="primary"
                                size="small"
                                sx={{ fontSize: "0.75rem" }}
                              />
                            </Stack>
                          </Card>
                        ))}
                      </Box>
                    )}
                  </Paper>
                )}
              </Paper>
            </Stack>
          </Grid>
        </Grid>
      </Box>
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Create Notification</DialogTitle>
        <DialogContent>
          <TextField
            label="Title"
            fullWidth
            margin="dense"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <TextField
            label="Message"
            fullWidth
            margin="dense"
            multiline
            rows={4}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
          <FormControl fullWidth margin="dense">
            <InputLabel>Recipient Scope</InputLabel>
            <Select
              value={recipientScope}
              onChange={(e) => setRecipientScope(e.target.value)}
              label="Recipient Scope"
            >
              <MenuItem value="tenants">Tenants Only</MenuItem>
              <MenuItem value="landlords">Landlords Only</MenuItem>
              <MenuItem value="everyone">Everyone</MenuItem>
              <MenuItem value="custom">Special Users</MenuItem>
            </Select>
          </FormControl>
          {recipientScope === "custom" && (
            <Box>
              <Autocomplete
                multiple
                options={userList}
                groupBy={(option) => option.role}
                getOptionLabel={(option) =>
                  `${option.FullName} (${option.phone} / ${option.email})`
                }
                filterSelectedOptions
                value={userList.filter((u) => toUserIds.includes(u.id))}
                onChange={(event, newValue) => {
                  setToUserIds(newValue.map((u) => u.id));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search Special Users"
                    placeholder="Search by name, email, username"
                    margin="normal"
                  />
                )}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox checked={selected} style={{ marginRight: 8 }} />
                    {option.avatar_url ? (
                      <Avatar
                        className="notranslate"
                        src={option.avatar_url}
                        sx={{ width: 24, height: 24, mr: 1 }}
                      />
                    ) : (
                      <Avatar
                        sx={{ bgcolor: stringToColor(option.FullName) }}
                        className="notranslate"
                      >
                        {option.FullName.charAt(0).toUpperCase()}
                      </Avatar>
                    )}
                    <Box ml={2} className="notranslate">
                      <Typography variant="body1">
                        {option.FullName} ({option.role})
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.email} / {option.phone}
                      </Typography>
                    </Box>
                  </li>
                )}
              />
            </Box>
          )}
          <FormControlLabel
            control={
              <Switch
                checked={sendNow}
                color="info"
                onChange={() => setSendNow(!sendNow)}
              />
            }
            label="Send Now"
          />
          {!sendNow && (
            <LocalizationProvider
              dateAdapter={AdapterDayjs}
              adapterLocale={i18n.language === "ar" ? "ar" : "en"} // Change dynamically
            >
              <DateTimePicker
                label={i18n.language === "ar" ? "تاريخ" : "Date"}
                value={scheduleAt ? dayjs(scheduleAt) : null}
                onChange={(newValue) =>
                  setScheduleAt(
                    newValue ? newValue.format("YYYY-MM-DD HH:mm") : ""
                  )
                }
                slotProps={{
                  textField: {
                    placeholder: "", // ✅ Hide placeholder
                    fullWidth: true,
                  },
                }}
              />
            </LocalizationProvider>
            // <TextField
            //   type="datetime-local"
            //   label="Schedule At"
            //   fullWidth
            //   value={scheduleAt}
            //   onChange={(e) => setScheduleAt(e.target.value)}
            //   InputLabelProps={{ shrink: true }}
            //   margin="dense"
            // />
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="success"
            sx={{ color: "#fff" }}
          >
            {isSubmit ? "Send..." : "Send"}
          </Button>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={confirmDelete}
        title="Confirm Deletion"
        content={`Are you sure you want to delete?`}
      />

      <Dialog open={openViewDialog} onClose={() => setOpenViewDialog(false)}>
        <DialogTitle>{selectedNotification?.title}</DialogTitle>
        <DialogContent>
          <Typography>{selectedNotification?.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenViewDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        onClose={() => setSnack({ ...snack, open: false })}
      >
        <Alert severity={snack.severity}>{snack.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default NotificationPage;
